import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Box, Text, TextInput, Textarea, Button, Group, Stack, SimpleGrid, Image, FileButton, Badge, ActionIcon, Divider, Loader, Select } from '@mantine/core'
import { IconPhoto, IconUpload, IconTrash, IconRefresh, IconBrandLine, IconMail, IconPalette, IconCopy, IconCheck } from '@tabler/icons-react'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { useBreakpoint } from '../hooks/useBreakpoint'
import { useAuth } from '../hooks/useAuth'
import { useWorkOrderMeta } from '../hooks/useWorkOrderMeta'
import { THEME_COLOR_PRESETS, THEME_COLOR_VALUES, ThemeColorPreset } from '../App'
import {
  getCompanyProfile, updateCompanyProfile, uploadCompanyLogo,
  getLineGroups, unlinkLineGroup, regenerateLineCode,
  getEmailConfig, verifyEmailConn, sendTestEmail,
} from '../api/client'

const EMPTY = {
  name: '', legalName: '', taxId: '', address: '', phone: '', email: '',
}

export default function CompanyProfilePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isMobile, isSmall } = useBreakpoint()
  const { updateBranding, companyThemeColor } = useAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [form, setForm] = useState({ ...EMPTY })
  const [logoUrl, setLogoUrl] = useState<string | null>(null)

  // Theme
  const [theme, setTheme] = useState<ThemeColorPreset>((companyThemeColor as ThemeColorPreset) || 'navy')
  const [savingTheme, setSavingTheme] = useState(false)

  // LINE
  const [lineCode, setLineCode] = useState<string | null>(null)
  const [lineGroups, setLineGroups] = useState<any[]>([])
  const [lineLoading, setLineLoading] = useState(true)
  const { departmentOptions, deptLabel } = useWorkOrderMeta()
  const [lineDept, setLineDept] = useState<string | null>(null)
  const [regenerating, setRegenerating] = useState(false)

  // Email
  const [emailConfig, setEmailConfig] = useState<any>(null)
  const [emailLoading, setEmailLoading] = useState(true)
  const [testTo, setTestTo] = useState('')
  const [sendingTest, setSendingTest] = useState(false)
  const [verifying, setVerifying] = useState(false)

  const load = async () => {
    setLoading(true)
    try {
      const { data } = await getCompanyProfile()
      setForm({
        name: data.name ?? '',
        legalName: data.legalName ?? '',
        taxId: data.taxId ?? '',
        address: data.address ?? '',
        phone: data.phone ?? '',
        email: data.email ?? '',
      })
      setLogoUrl(data.logoUrl ?? null)
      setLineCode(data.lineLinkCode ?? null)
      if (data.themeColor && (THEME_COLOR_PRESETS as readonly string[]).includes(data.themeColor)) {
        setTheme(data.themeColor as ThemeColorPreset)
      }
    } catch {
      notifications.show({ color: 'red', message: t('companyProfile.loadError') })
    } finally { setLoading(false) }
  }

  const loadLineGroups = async () => {
    setLineLoading(true)
    try {
      const { data } = await getLineGroups()
      setLineGroups(data ?? [])
    } catch { /* non-fatal */ } finally { setLineLoading(false) }
  }

  const loadEmailConfig = async () => {
    setEmailLoading(true)
    try {
      const { data } = await getEmailConfig()
      setEmailConfig(data)
    } catch { /* non-fatal */ } finally { setEmailLoading(false) }
  }

  useEffect(() => { load(); loadLineGroups(); loadEmailConfig() }, [])

  const f = (k: string) => (e: any) => setForm((p) => ({ ...p, [k]: e.target.value }))

  const handleSave = async () => {
    setSaving(true)
    try {
      await updateCompanyProfile(form)
      updateBranding({ companyName: form.name })
      notifications.show({ color: 'lime', message: t('companyProfile.saveSuccess') })
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t('companyProfile.saveError') })
    } finally { setSaving(false) }
  }

  const handleLogoChange = async (file: File | null) => {
    if (!file) return
    setUploading(true)
    try {
      const { data } = await uploadCompanyLogo(file)
      setLogoUrl(data.logoUrl ?? null)
      updateBranding({ companyLogoUrl: data.logoUrl ?? '' })
      notifications.show({ color: 'lime', message: t('companyProfile.uploadSuccess') })
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t('companyProfile.uploadError') })
    } finally { setUploading(false) }
  }

  const handleThemeSelect = async (preset: ThemeColorPreset) => {
    setTheme(preset)
    setSavingTheme(true)
    try {
      await updateCompanyProfile({ themeColor: preset } as any)
      updateBranding({ companyThemeColor: preset })
      notifications.show({ color: 'lime', message: t('companyProfile.saveSuccess') })
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t('companyProfile.saveError') })
    } finally { setSavingTheme(false) }
  }

  const copyButton = (cmd: string) => (
    <Button size="compact-xs" variant="outline" color="gray" leftSection={<IconCopy size={12} />}
      onClick={async () => {
        try { await navigator.clipboard.writeText(cmd); notifications.show({ color: 'lime', message: t('companyProfile.lineCopied') }) }
        catch { notifications.show({ color: 'red', message: cmd }) }
      }}>
      {t('companyProfile.lineCopy')}
    </Button>
  )

  const renderLineRow = (key: string, label: string, linked: any[], cmd: string, hint?: string) => (
    <Box key={key} style={{ padding: '10px 12px', background: '#fafafa', borderRadius: 8 }}>
      <Group gap={8}>
        <Text size="sm" fw={600}>{label}</Text>
        {linked.length > 0
          ? <Badge size="sm" color="teal" variant="light" leftSection={<IconCheck size={12} />}>{t('companyProfile.lineConnected')}</Badge>
          : <Badge size="sm" color="gray" variant="light">{t('companyProfile.lineNotConnected')}</Badge>}
      </Group>
      {hint && <Text size="xs" c="dimmed">{hint}</Text>}
      {linked.map((g: any) => (
        <Group key={g.id} justify="space-between" mt={6} wrap="nowrap">
          <Text size="sm">{g.groupName || g.lineGroupId}</Text>
          <Button size="compact-xs" color="red" variant="light" leftSection={<IconTrash size={12} />}
            onClick={() => handleUnlinkGroup(g)}>
            {t('companyProfile.unlinkGroup')}
          </Button>
        </Group>
      ))}
      {cmd && (
        <Group gap={6} mt={8} wrap="nowrap">
          <Text size="xs" style={{ fontFamily: 'monospace', flex: 1, wordBreak: 'break-all' }}>{cmd}</Text>
          {copyButton(cmd)}
        </Group>
      )}
    </Box>
  )

  const renderOrphanGroups = () => {
    const known = new Set(departmentOptions.map(d => d.value))
    const orphans = lineGroups.filter((g: any) => g.orderType !== 'hkorder' && g.departmentCode && !known.has(g.departmentCode))
    return orphans.length ? renderLineRow('ORPHAN', 'Work Order', orphans, '') : null
  }

  const handleUnlinkGroup = (group: any) => {
    modals.openConfirmModal({
      title: t('companyProfile.unlinkGroup'),
      children: <Text size="sm">{group.groupName || group.lineGroupId}</Text>,
      labels: { confirm: t('companyProfile.unlinkGroup'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await unlinkLineGroup(group.id)
          notifications.show({ color: 'lime', message: t('companyProfile.unlinkGroupSuccess') })
          loadLineGroups()
        } catch (e: any) {
          notifications.show({ color: 'red', message: e.response?.data?.message ?? t('companyProfile.saveError') })
        }
      },
    })
  }

  const handleRegenerateCode = () => {
    modals.openConfirmModal({
      title: t('companyProfile.regenerateConfirmTitle'),
      children: <Text size="sm">{t('companyProfile.regenerateConfirmBody')}</Text>,
      labels: { confirm: t('companyProfile.regenerateCode'), cancel: t('common.cancel') },
      confirmProps: { color: 'orange' },
      onConfirm: async () => {
        setRegenerating(true)
        try {
          const { data } = await regenerateLineCode()
          setLineCode(data.lineLinkCode ?? null)
          notifications.show({ color: 'lime', message: t('companyProfile.lineCodeRegenerated') })
        } catch (e: any) {
          notifications.show({ color: 'red', message: e.response?.data?.message ?? t('companyProfile.saveError') })
        } finally { setRegenerating(false) }
      },
    })
  }

  const handleVerifyEmail = async () => {
    setVerifying(true)
    try {
      const { data } = await verifyEmailConn()
      notifications.show({
        color: data.success ? 'lime' : 'red',
        message: data.success ? t('companyProfile.emailVerifySuccess') : (data.message || t('companyProfile.emailVerifyFailed')),
      })
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t('companyProfile.emailVerifyFailed') })
    } finally { setVerifying(false) }
  }

  const handleSendTestEmail = async () => {
    if (!testTo) return
    setSendingTest(true)
    try {
      const { data } = await sendTestEmail(testTo)
      notifications.show({
        color: data.success ? 'lime' : 'red',
        message: data.success ? t('companyProfile.emailTestSent') : (data.message || t('companyProfile.emailVerifyFailed')),
      })
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t('companyProfile.emailVerifyFailed') })
    } finally { setSendingTest(false) }
  }

  return (
    <Box p={isSmall ? 16 : 32} maw={isMobile ? '100%' : 720}>
      <Box mb={isMobile ? 'md' : 'xl'}>
        <Text style={{ fontFamily: 'Inter, sans-serif', fontSize: isSmall ? 20 : 24, fontWeight: 600, letterSpacing: '-0.3px' }}>
          {t('companyProfile.title')}
        </Text>
        <Text size="sm" c="dimmed">{t('companyProfile.subtitle')}</Text>
      </Box>

      {/* โลโก้ */}
      <Box style={{ background: '#fff', borderRadius: 16, border: '1px solid #ebebeb', padding: isMobile ? 16 : 24 }} mb="lg">
        <Text fw={700} mb="md">{t('companyProfile.logoTitle')}</Text>
        <Group align="flex-start" gap="md">
          <Box w={120} h={120} style={{
            background: '#f8f7f4', borderRadius: 10, border: '1.5px dashed #ddd',
            display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0,
          }}>
            {logoUrl ? <Image src={logoUrl} w={120} h={120} fit="contain" /> : <IconPhoto size={32} color="#ccc" />}
          </Box>
          <Stack gap={6}>
            <FileButton onChange={handleLogoChange} accept="image/png,image/jpeg,image/webp">
              {(props) => (
                <Button {...props} loading={uploading} variant="outline" size="xs" leftSection={<IconUpload size={14} />} color="gray">
                  {logoUrl ? t('companyProfile.changeLogo') : t('companyProfile.uploadLogo')}
                </Button>
              )}
            </FileButton>
            <Text size="xs" c="dimmed">{t('companyProfile.logoHint')}</Text>
          </Stack>
        </Group>
      </Box>

      {/* ฟอร์มข้อมูลบริษัท */}
      <Box style={{ background: '#fff', borderRadius: 16, border: '1px solid #ebebeb', padding: isMobile ? 16 : 24 }}>
        <Text fw={700} mb="md">{t('companyProfile.reportInfoTitle')}</Text>
        <Stack gap="sm">
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput label={t('companyProfile.displayName')} value={form.name} onChange={f('name')} disabled={loading} />
            <TextInput label={t('companyProfile.legalName')} placeholder={t('companyProfile.legalNamePlaceholder')} value={form.legalName} onChange={f('legalName')} disabled={loading} />
          </SimpleGrid>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput label={t('companyProfile.taxId')} value={form.taxId} onChange={f('taxId')} disabled={loading} />
            <TextInput label={t('companyProfile.phone')} value={form.phone} onChange={f('phone')} disabled={loading} />
          </SimpleGrid>
          <TextInput label={t('companyProfile.email')} value={form.email} onChange={f('email')} disabled={loading} />
          <Textarea label={t('companyProfile.address')} minRows={3} value={form.address} onChange={f('address')} disabled={loading} />

          <Group justify="flex-end" mt="sm">
            <Button loading={saving} disabled={loading} onClick={handleSave}
              style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #ffffff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>
              {t('common.save')}
            </Button>
          </Group>
        </Stack>
      </Box>

      {/* ธีมสี */}
      <Box style={{ background: '#fff', borderRadius: 16, border: '1px solid #ebebeb', padding: isMobile ? 16 : 24 }} mt="lg">
        <Group gap={6} mb={4}><IconPalette size={18} /><Text fw={700}>{t('companyProfile.themeTitle')}</Text></Group>
        <Text size="xs" c="dimmed" mb="md">{t('companyProfile.themeHint')}</Text>
        <Group gap="sm">
          {THEME_COLOR_PRESETS.map((preset) => {
            const v = THEME_COLOR_VALUES[preset]
            const selected = theme === preset
            return (
              <Box key={preset} onClick={() => !savingTheme && handleThemeSelect(preset)}
                style={{
                  width: 56, height: 56, borderRadius: 10, background: v.swatch, cursor: 'pointer',
                  border: selected ? '3px solid #0d9488' : `1.5px solid ${v.border === 'transparent' ? '#ddd' : v.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: savingTheme ? 0.6 : 1, transition: 'all .15s',
                }}
              >
                {selected && <Text size="xs" style={{ color: v.text }}>✓</Text>}
              </Box>
            )
          })}
          {savingTheme && <Loader size="xs" />}
        </Group>
      </Box>

      {/* LINE Integration */}
      <Box style={{ background: '#fff', borderRadius: 16, border: '1px solid #ebebeb', padding: isMobile ? 16 : 24 }} mt="lg">
        <Group gap={6} mb={4}><IconBrandLine size={18} /><Text fw={700}>{t('companyProfile.lineTitle')}</Text></Group>
        <Text size="xs" c="dimmed" mb="md">{t('companyProfile.lineHint')}</Text>

        <Stack gap={4} mb="md">
          <Text size="sm" c="dimmed">{t('companyProfile.lineCodeLabel')}</Text>
          <Group gap="sm">
            <Badge size="lg" variant="light" color="teal" style={{ fontFamily: 'monospace', fontSize: 14 }}>
              {lineCode ?? '—'}
            </Badge>
            <Button size="xs" variant="outline" color="gray" loading={regenerating}
              leftSection={<IconRefresh size={14} />} onClick={handleRegenerateCode}>
              {t('companyProfile.regenerateCode')}
            </Button>
          </Group>
          {lineCode && (
            <Text size="xs" c="dimmed" mt={4}>
              {t('companyProfile.lineCodeInstruction', { code: lineCode })}
            </Text>
          )}
        </Stack>

        <Divider my="sm" />

        <Text size="sm" fw={600} mb={8}>{t('companyProfile.lineLinkedGroups')}</Text>
        {lineLoading ? (
          <Loader size="xs" />
        ) : (
          <Stack gap={8}>
            {([
              { key: 'HK', cmdSuffix: ' HK', label: 'HK Order', match: (g: any) => g.orderType === 'hkorder' },
              { key: 'WO', cmdSuffix: '', label: t('companyProfile.lineWoAll'), hint: t('companyProfile.lineAllDepts'), match: (g: any) => g.orderType !== 'hkorder' && !g.departmentCode },
            ]).map(row => {
              const linked = lineGroups.filter(row.match)
              const cmd = lineCode ? `เชื่อมกลุ่ม ${lineCode}${row.cmdSuffix}` : ''
              return renderLineRow(row.key, row.label, linked, cmd, (row as any).hint)
            })}

            {/* ── Work Order เฉพาะแผนก: เลือกแผนกแล้วสร้างคำสั่ง ── */}
            <Box style={{ padding: '10px 12px', background: '#fafafa', borderRadius: 8 }}>
              <Text size="sm" fw={600}>{t('companyProfile.lineWoDept')}</Text>
              <Text size="xs" c="dimmed" mb={6}>{t('companyProfile.lineDeptHint')}</Text>
              <Select
                size="xs" clearable searchable
                placeholder={t('companyProfile.lineDeptPickPlaceholder')}
                nothingFoundMessage={t('workOrders.noMatch')}
                data={departmentOptions} value={lineDept} onChange={setLineDept}
              />
              {lineCode && lineDept && (
                <Group gap={6} mt={8} wrap="nowrap">
                  <Text size="xs" style={{ fontFamily: 'monospace', flex: 1, wordBreak: 'break-all' }}>{`เชื่อมกลุ่ม ${lineCode} ${lineDept}`}</Text>
                  {copyButton(`เชื่อมกลุ่ม ${lineCode} ${lineDept}`)}
                </Group>
              )}
            </Box>
            {departmentOptions.map(d => ({ d, linked: lineGroups.filter((g: any) => g.orderType !== 'hkorder' && g.departmentCode === d.value) }))
              .filter(x => x.linked.length > 0)
              .map(x => renderLineRow(`DEPT-${x.d.value}`, `Work Order · ${deptLabel(x.d.value)}`, x.linked, ''))}
            {/* กลุ่มที่ผูกกับรหัสแผนกที่ไม่มีในรายการแล้ว (เช่นถูกลบ) ยังต้องยกเลิกเชื่อมต่อได้ */}
            {renderOrphanGroups()}
          </Stack>
        )}

        <Button variant="subtle" size="xs" mt="md" onClick={() => navigate('/reminder-settings')}>
          {t('companyProfile.reminderSettingsLink')} →
        </Button>
      </Box>

      {/* Email Settings */}
      <Box style={{ background: '#fff', borderRadius: 16, border: '1px solid #ebebeb', padding: isMobile ? 16 : 24 }} mt="lg">
        <Group gap={6} mb={4}><IconMail size={18} /><Text fw={700}>{t('companyProfile.emailTitle')}</Text></Group>
        <Text size="xs" c="dimmed" mb="md">{t('companyProfile.emailHint')}</Text>

        {emailLoading ? (
          <Loader size="xs" />
        ) : !emailConfig || emailConfig.mode === 'none' ? (
          <Text size="sm" c="dimmed">{t('companyProfile.emailNotConfigured')}</Text>
        ) : (
          <Stack gap={6} mb="md">
            <Group gap="xs"><Text size="sm" c="dimmed" w={110}>{t('companyProfile.emailMode')}</Text><Badge size="sm" variant="light">{emailConfig.mode}</Badge></Group>
            {emailConfig.host && <Group gap="xs"><Text size="sm" c="dimmed" w={110}>{t('companyProfile.emailHost')}</Text><Text size="sm">{emailConfig.host}</Text></Group>}
            {emailConfig.from && <Group gap="xs"><Text size="sm" c="dimmed" w={110}>{t('companyProfile.emailFrom')}</Text><Text size="sm">{emailConfig.from}</Text></Group>}
          </Stack>
        )}

        <Group gap="sm" mb="sm">
          <Button size="xs" variant="outline" color="gray" loading={verifying} onClick={handleVerifyEmail}>
            {t('companyProfile.emailVerify')}
          </Button>
        </Group>

        <Group gap="sm" align="flex-end">
          <TextInput size="xs" label={t('companyProfile.emailTestTo')} placeholder="you@example.com"
            value={testTo} onChange={(e) => setTestTo(e.target.value)} style={{ flex: 1 }} />
          <Button size="xs" loading={sendingTest} disabled={!testTo} onClick={handleSendTestEmail}
            style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #ffffff)', border: '1px solid var(--brand-border, transparent)' }}>
            {t('companyProfile.emailSendTest')}
          </Button>
        </Group>
      </Box>
    </Box>
  )
}
